import { cn } from '@/lib/utils';
import { ChevronRight, AlertCircle, UserCheck, SearchX, CheckCircle2, Ban } from 'lucide-react';
import { OrderByComment, OrderByCommentDetectState } from '@/types/fb-type';

interface OrderDetectionTagProps {
  data: OrderByComment;
  className?: string;
  compact?: boolean;
  dense?: boolean;
}

const STATE_CONFIG = {
  [OrderByCommentDetectState.SUCCESS]: {
    color: 'border-green-500 bg-white hover:bg-green-50 ring-slate-200',
    icon: <CheckCircle2 className="h-3 w-3 shrink-0 text-green-500" />,
    labelColor: 'text-green-700 bg-green-100',
  },
  [OrderByCommentDetectState.DETECT_ERROR]: {
    color: 'border-red-500 bg-red-50 hover:bg-red-100 ring-red-100',
    icon: <AlertCircle className="h-3 w-3 shrink-0 text-red-500" />,
    labelColor: 'text-red-700 bg-red-200',
  },
  [OrderByCommentDetectState.MANUAL]: {
    color: 'border-purple-500 bg-white hover:bg-purple-50 ring-purple-100',
    icon: <UserCheck className="h-3 w-3 shrink-0 text-purple-500" />,
    labelColor: 'text-purple-700 bg-purple-100',
  },
  [OrderByCommentDetectState.NOT_DETECT]: {
    color: 'border-slate-300 bg-slate-50 opacity-90 ring-slate-200',
    icon: <SearchX className="h-3 w-3 shrink-0 text-slate-400" />,
    labelColor: 'text-slate-600 bg-slate-200',
  },
};

function OrderDetailField({
  label,
  value,
  valueClassName,
}: {
  label: string;
  value: string;
  valueClassName?: string;
}) {
  return (
    <div className="min-w-0 max-w-full">
      <span className="text-[9px] font-bold uppercase text-slate-400">{label}</span>
      <p
        className={cn('truncate text-[11px] font-bold leading-tight', valueClassName)}
        title={value}
      >
        {value}
      </p>
    </div>
  );
}

export function OrderDetectionTag({
  data,
  className,
  compact = false,
  dense = false,
}: OrderDetectionTagProps) {
  if (data.customerBlocked) {
    return (
      <div
        className={cn(
          'flex w-full min-w-0 max-w-full items-start gap-1 rounded border-l-[3px] border-red-600 bg-red-100 p-1.5 text-red-700 ring-1 ring-red-200',
          className,
        )}
      >
        <Ban className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <div className="min-w-0">
          <span className="rounded bg-red-600 px-1 text-[8px] font-black uppercase text-white">
            Blocked customer
          </span>
          <p className="break-words text-[10px] font-semibold">
            {data.customerName ? `${data.customerName} tried to order. ` : 'Tried to order. '}
            {data.errorMsg?.replace('BLOCKED CUSTOMER: ', '')}
          </p>
        </div>
      </div>
    );
  }

  const detectState = data.detectState ?? OrderByCommentDetectState.NOT_DETECT;
  const config = STATE_CONFIG[detectState] ?? STATE_CONFIG[OrderByCommentDetectState.NOT_DETECT];
  const isError = detectState === OrderByCommentDetectState.DETECT_ERROR;
  const isNotDetect = detectState === OrderByCommentDetectState.NOT_DETECT;
  const isClickable = !isError && !isNotDetect;
  const tight = compact || dense;

  const orderLabel = `#${data.orderNumber || '---'}`;
  const codeLabel = data.saleCode || 'N/A';
  const totalLabel = `$${data.totalAmount?.toLocaleString() ?? '0'}`;

  const contentClasses = cn(
    'group flex min-w-0 w-full max-w-full rounded border-l-[3px] transition-all',
    dense
      ? 'items-center gap-1 p-1'
      : tight
        ? 'items-center gap-1.5 p-1'
        : 'flex-col gap-2 p-2 sm:flex-row sm:items-center sm:gap-2.5',
    tight ? 'shadow-none ring-0' : 'shadow-sm ring-1',
    isClickable && 'cursor-pointer active:scale-[0.98]',
    !isClickable && 'cursor-default',
    config.color,
  );

  const statusBadge = (
    <div className="flex shrink-0 items-center gap-0.5">
      {config.icon}
      <span className={cn('rounded px-1 text-[8px] font-black uppercase', config.labelColor)}>
        {detectState.split('_')[0]}
      </span>
    </div>
  );

  const tightDetails = (
    <div
      className={cn(
        'flex min-w-0 flex-1 items-center overflow-hidden leading-none',
        dense ? 'gap-1 text-[9px]' : 'gap-1.5 text-[10px]',
      )}
    >
      <span className="truncate font-mono font-bold text-blue-700" title={orderLabel}>
        {orderLabel}
      </span>
      <span className="shrink-0 text-slate-300">·</span>
      <span className="truncate font-bold text-slate-800" title={codeLabel}>
        {codeLabel}
      </span>
      <span className="shrink-0 text-slate-300">·</span>
      <span className="shrink-0 font-bold text-green-600">{totalLabel}</span>
    </div>
  );

  const wideDetails = (
    <div className="grid min-w-0 flex-1 grid-cols-2 gap-x-2 gap-y-1 sm:flex sm:flex-wrap sm:items-center sm:gap-x-3 sm:gap-y-1">
      <OrderDetailField
        label="Order"
        value={orderLabel}
        valueClassName="font-mono text-blue-700"
      />
      <OrderDetailField label="Code" value={codeLabel} valueClassName="text-slate-800" />
      <div className="col-span-2 sm:col-span-1">
        <OrderDetailField
          label="Total"
          value={totalLabel}
          valueClassName="text-green-600"
        />
      </div>
    </div>
  );

  const content = tight ? (
    <>
      {statusBadge}
      {tightDetails}
      {isClickable && (
        <ChevronRight className="h-3 w-3 shrink-0 text-slate-300 transition-colors group-hover:text-slate-500" />
      )}
    </>
  ) : (
    <>
      <div className="flex w-full items-center justify-between gap-1">
        {statusBadge}
        {isClickable && (
          <ChevronRight className="h-3.5 w-3.5 shrink-0 text-slate-300 transition-colors group-hover:text-slate-500" />
        )}
      </div>
      {wideDetails}
    </>
  );

  return (
    <div className={cn('flex w-full min-w-0 max-w-full flex-col overflow-hidden', className)}>
      {isClickable ? (
        <a href={`/orders/${data.orderId}`} className={contentClasses}>
          {content}
        </a>
      ) : (
        <div className={contentClasses}>{content}</div>
      )}

      {isError && data.errorMsg && (
        <div className="mt-1 flex items-start gap-1 text-red-500">
          <AlertCircle className="mt-0.5 h-3 w-3 shrink-0" />
          <span className="break-words text-[10px] font-medium italic">{data.errorMsg}</span>
        </div>
      )}
    </div>
  );
}
